import React, { useState, useEffect, useCallback, useMemo, useContext } from "react";
import {
  X, Package, Plus, RefreshCw, Trash2, Search, BadgePercent, ShoppingCart,
} from "lucide-react";
import api from "../utils/api";
import { toast } from "react-toastify";
import { useAuth } from "./authentication/AuthContext";
import { ItemsContext } from "../context/ItemsContext";
import { formatIndianCurrency } from "../utils/Const";

const fmt = (n) => `₹${formatIndianCurrency(n || 0)}`;

export default function CombosPanel({ onClose, onAddCombo, hasActiveOrder }) {
  const { isStaff, staffRole } = useAuth();
  const isManager = !isStaff || staffRole === "manager";
  const { items } = useContext(ItemsContext);
  const products = useMemo(() => (Array.isArray(items) ? items : items?.data || []), [items]);

  const [combos, setCombos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [name, setName] = useState("");
  const [comboPrice, setComboPrice] = useState("");
  const [picked, setPicked] = useState([]); // {productId, name, qty, price}
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchCombos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/combos");
      setCombos(res.data.combos || []);
    } catch {
      toast.error("Failed to load combos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCombos(); }, [fetchCombos]);

  const pickedTotal = picked.reduce((s, it) => s + it.price * it.qty, 0);
  const savings = pickedTotal && comboPrice ? pickedTotal - Number(comboPrice) : 0;

  const addProduct = (p) => {
    if (!p.price && p.variants?.length) {
      toast.info("Variant products use their base price in combos — set one first");
      return;
    }
    setPicked(prev => {
      const idx = prev.findIndex(it => it.productId === p._id);
      if (idx >= 0) return prev.map((it, n) => n === idx ? { ...it, qty: it.qty + 1 } : it);
      return [...prev, { productId: p._id, name: p.productName, qty: 1, price: Number(p.price) || 0 }];
    });
  };

  const changeQty = (i, delta) => {
    setPicked(prev => prev
      .map((it, n) => n === i ? { ...it, qty: it.qty + delta } : it)
      .filter(it => it.qty > 0));
  };

  const saveCombo = async () => {
    if (!name.trim() || picked.length < 2 || !comboPrice) {
      toast.error("Name, at least 2 items and a combo price are required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/combos", { name, items: picked, comboPrice: Number(comboPrice) });
      toast.success("Combo created");
      setShowNew(false);
      setName(""); setComboPrice(""); setPicked([]);
      fetchCombos();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create combo");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (combo) => {
    try {
      await api.put(`/combos/${combo._id}`, { isActive: !combo.isActive });
      fetchCombos();
    } catch {
      toast.error("Failed to update combo");
    }
  };

  const removeCombo = async (combo) => {
    try {
      await api.delete(`/combos/${combo._id}`);
      toast.success("Combo deleted");
      fetchCombos();
    } catch {
      toast.error("Failed to delete combo");
    }
  };

  const filteredProducts = products.filter(p =>
    p.isAvailable !== false &&
    (!search.trim() || p.productName?.toLowerCase().includes(search.trim().toLowerCase()))
  ).slice(0, 30);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-orange-500/15 border border-orange-500/25 flex items-center justify-center">
              <Package size={15} className="text-orange-500" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-800">Combo Deals</h2>
              <p className="text-[10px] text-slate-400">Bundle items at a special price</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isManager && (
              <button onClick={() => setShowNew(v => !v)}
                className="flex items-center gap-1.5 text-xs font-bold bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-xl transition-colors">
                <Plus size={12} /> New Combo
              </button>
            )}
            <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
              <X size={14} />
            </button>
          </div>
        </div>

        {/* New combo builder */}
        {showNew && (
          <div className="border-b border-slate-100 px-5 py-4 space-y-3 bg-orange-50/40 max-h-[45vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Combo name (e.g. Burger Meal)" value={name} onChange={e => setName(e.target.value)}
                className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-orange-500" />
              <input type="number" min="0" placeholder="Combo price ₹" value={comboPrice} onChange={e => setComboPrice(e.target.value)}
                className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-orange-500" />
            </div>

            {/* Picked items */}
            {picked.length > 0 && (
              <div className="space-y-1">
                {picked.map((it, i) => (
                  <div key={it.productId} className="flex items-center gap-2 bg-white rounded-lg border border-slate-200 px-2.5 py-1.5">
                    <span className="flex-1 text-xs font-semibold text-slate-700 truncate">{it.name}</span>
                    <span className="text-[10px] text-slate-400">{fmt(it.price)} ×</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => changeQty(i, -1)} className="w-5 h-5 rounded-full bg-slate-100 text-slate-600 text-xs">−</button>
                      <span className="text-xs font-bold w-4 text-center">{it.qty}</span>
                      <button onClick={() => changeQty(i, 1)} className="w-5 h-5 rounded-full bg-orange-500 text-white text-xs">+</button>
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between px-1 pt-1">
                  <p className="text-[11px] text-slate-500">Items total: <strong>{fmt(pickedTotal)}</strong></p>
                  {comboPrice && savings > 0 && (
                    <p className="text-[11px] font-bold text-emerald-600 flex items-center gap-1">
                      <BadgePercent size={11} /> Customer saves {fmt(savings)} ({Math.round((savings / pickedTotal) * 100)}%)
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Product picker */}
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input placeholder="Search products to add…" value={search} onChange={e => setSearch(e.target.value)}
                className="w-full pl-7 pr-2 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-orange-500" />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {filteredProducts.map(p => (
                <button key={p._id} onClick={() => addProduct(p)}
                  className="text-[10px] font-semibold px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:border-orange-300 hover:bg-orange-50">
                  {p.productName} · ₹{p.price || (p.variants?.[0]?.price ?? 0)}
                </button>
              ))}
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={() => setShowNew(false)} className="px-3 py-1.5 text-xs font-semibold bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200">Cancel</button>
              <button onClick={saveCombo} disabled={saving}
                className="px-4 py-1.5 text-xs font-bold bg-orange-500 hover:bg-orange-600 text-white rounded-xl disabled:opacity-40">
                {saving ? "Saving…" : "Save Combo"}
              </button>
            </div>
          </div>
        )}

        {/* Combo list */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-2.5">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <RefreshCw size={18} className="animate-spin text-slate-400" />
            </div>
          ) : combos.length === 0 ? (
            <div className="text-center py-10">
              <Package size={32} className="mx-auto text-slate-200 mb-2" />
              <p className="text-xs text-slate-400">No combos yet{isManager ? " — create your first bundle deal" : ""}</p>
            </div>
          ) : combos.map(combo => {
            const itemsTotal = combo.items.reduce((s, it) => s + it.price * it.qty, 0);
            const save = itemsTotal - combo.comboPrice;
            return (
              <div key={combo._id} className={`rounded-xl border p-3 ${combo.isActive ? "border-orange-200 bg-orange-50/30" : "border-slate-200 bg-slate-50 opacity-60"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-black text-slate-800">{combo.name}</p>
                      <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                        Save {fmt(save)}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      {combo.items.map(it => `${it.qty}× ${it.name}`).join(" + ")}
                    </p>
                    <p className="text-[11px] mt-1">
                      <span className="line-through text-slate-400">{fmt(itemsTotal)}</span>{" "}
                      <span className="font-black text-orange-600">{fmt(combo.comboPrice)}</span>
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    {combo.isActive && onAddCombo && (
                      <button
                        onClick={() => {
                          if (!hasActiveOrder) { toast.info("Select an order/table first"); return; }
                          onAddCombo(combo);
                          toast.success(`${combo.name} added to order`);
                        }}
                        className="flex items-center gap-1.5 text-[10px] font-bold px-3 py-1.5 rounded-lg bg-orange-500 text-white hover:bg-orange-600"
                      >
                        <ShoppingCart size={11} /> Add to order
                      </button>
                    )}
                    {isManager && (
                      <div className="flex gap-1.5">
                        <button onClick={() => toggleActive(combo)}
                          className={`text-[10px] font-bold px-2 py-1 rounded-lg ${combo.isActive ? "bg-slate-100 text-slate-500" : "bg-emerald-100 text-emerald-600"}`}>
                          {combo.isActive ? "Disable" : "Enable"}
                        </button>
                        <button onClick={() => removeCombo(combo)}
                          className="w-6 h-6 flex items-center justify-center rounded-lg bg-rose-50 text-rose-500 hover:bg-rose-100">
                          <Trash2 size={11} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between gap-2">
          <p className="text-[10px] text-slate-400">
            Combos expand into their items on the KOT & bill with prices scaled to the combo total — savings show automatically.
          </p>
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 shrink-0">Close</button>
        </div>
      </div>
    </div>
  );
}
